/**
 * @file browserUtils.js
 * @description Browser detection utilities for PWA installation
 */

/**
 * Detect if the user is on iOS (any browser)
 * @returns {boolean} True if running on iOS
 */
export const isIOS = () => {
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent);
};

/**
 * Detect if the user is on iOS Safari specifically
 * @returns {boolean} True if running on iOS Safari
 */
export const isIOSSafari = () => {
  const userAgent = navigator.userAgent;
  const isIOSDevice = isIOS();
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(userAgent);
  return isIOSDevice && isSafari;
};

/**
 * Detect if the user is on Android Chrome
 * @returns {boolean} True if running on Android Chrome
 */
export const isAndroidChrome = () => {
  const userAgent = navigator.userAgent;
  const isAndroid = /Android/.test(userAgent);
  const isChrome = /Chrome/.test(userAgent) && !/EdgiOS/.test(userAgent);
  return isAndroid && isChrome;
};

/**
 * Detect if the browser supports PWA installation prompts
 * @returns {boolean} True if beforeinstallprompt is supported
 */
export const supportsInstallPrompt = () => {
  return 'BeforeInstallPromptEvent' in window || 'onbeforeinstallprompt' in window;
};

/**
 * Get installation instructions based on the current browser/platform
 * @returns {object} Installation instructions with title and steps
 */
export const getInstallInstructions = () => {
  if (isIOS()) {
    // All iOS browsers (Safari, Chrome, Firefox, etc.) use the same installation method
    return {
      title: 'Add to Home Screen',
      platform: 'iOS',
      description: `On iOS, PWA features like notifications and offline access are only available after adding the app to your home screen. All browsers on iOS (Safari, Chrome, Firefox, etc.) use the same installation method.`,
      steps: [
        `Tap the Share button ${isIOSSafari() ? 'at the bottom of the screen' : 'in your browser menu'}`,
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner',
        'The app will appear on your home screen with full PWA features'
      ],
      iconName: 'iosShare',
      note: 'After installation, you\'ll have access to notifications, offline reading, and a native app experience.'
    };
  }
  
  if (isAndroidChrome()) {
    return {
      title: 'Install App',
      platform: 'Android Chrome',
      description: 'Install memorize.life as a native app for the best experience.',
      steps: [
        'Tap the menu button (three dots) in the top right',
        'Select "Add to Home screen" or "Install app"',
        'Tap "Add" or "Install" to confirm',
        'The app will be added to your home screen'
      ],
      iconName: 'menuMore'
    };
  }
  
  // Desktop or other browsers
  return {
    title: 'Install App',
    platform: 'Desktop Browser',
    description: 'Install memorize.life for quick access and offline reading.',
    steps: [
      'Look for an install icon in your browser\'s address bar',
      'Or check your browser\'s menu for "Install" or "Add to Home Screen"',
      'Follow the prompts to install the app',
      'The app will be available in your applications'
    ],
    iconName: 'install'
  };
};
